import { Form, FormField, FormFieldConditions, FormSection, FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import { DropdownData, InputType } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import { list } from 'src/app/common/classes/request-builder';
import { ClienteModel } from 'src/app/models/cliente.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { UserModel } from 'src/app/models/usuarios/user.model';
import { environment } from 'src/environments/environment';

/** Profile forms rendered in the navbar dialog. */
export class NavbarProfileFormDefinition {
  public readonly userFormFields: Form<UserModel>;
  public readonly userExtraFormFields: Form<UserModel>;
  public readonly clientFormFields: Form<ClienteModel>;

  private readonly cardTypes = list<string>(
    ['...', null],
    'BÁSICO',
    'CUALIFICADO',
    'FUMIGADOR',
    'PILOTO',
    'ASESOR'
  );

  private readonly controllerTypes = list<string>(
    ['...', null],
    'SISTRONIC',
    'TRONIC',
    'SISTROMATIC',
    'OZONO'
  );

  constructor(
    private readonly unidades: DropdownData<string>,
    private readonly fincas: DropdownData<FincasModel>
  ) {
    this.userFormFields = this.createForm<UserModel>('navbar-user-profile', this.getUserFields());
    this.userExtraFormFields = this.createForm<UserModel>('navbar-user-extra-profile', this.getUserExtraFields());
    this.clientFormFields = this.createForm<ClienteModel>('navbar-client-profile', this.getClientFields());
  }

  private createForm<T>(name: string, fields: FormField<T>[]): Form<T> {
    return {
      name,
      header: '',
      type: FormType.EDITION,
      sections: [this.createSection(fields)],
      table: null,
      uploadButton: { isEnabled: false, label: '' },
      signed: false,
    };
  }

  private createSection<T>(fields: FormField<T>[]): FormSection<T> {
    return {
      name: '',
      fields,
      width: 12,
      isVisible: true,
      order: 1,
    };
  }

  private getUserFields(): FormField<UserModel>[] {
    return [
      this.textField<UserModel>('nombre', 'Nombre/Razón social', 1, true, 6),
      this.textField<UserModel>('apellidos', 'Apellidos', 2, true, 6),
      this.textField<UserModel>('telefono', 'Teléfono', 3, environment.appName === 'agrogestSIEX', 6),
      this.dropdownField<UserModel, string>('tipo_carnet', 'Tipo de carnet', 4, this.cardTypes, environment.appName !== 'agrogestSIEX', 6),
      this.textField<UserModel>('fecha_caducidad', 'Fecha de caducidad', 5, environment.appName !== 'agrogestSIEX', 6, InputType.CALENDAR),
      this.textField<UserModel>('nif', 'CIF/NIF', 6, true, 6),
      this.textField<UserModel>('ropo', 'ROPO', 7, environment.appName !== 'agrogestSIEX', 6),
      this.textField<UserModel>('imei', 'IMEI', 8, environment.appName === 'manezylozano', 6),
      this.dropdownField<UserModel, string>('tipo_mando', 'Tipo de mando', 9, this.controllerTypes, environment.appName === 'manezylozano', 6),
    ];
  }

  private getUserExtraFields(): FormField<UserModel>[] {
    return [
      this.dropdownField<UserModel, string>('uds_medida', 'Unidad de medida', 1, this.unidades, environment.features.showPerfilUnidades, 6),
      this.textField<UserModel>(
        'ratio_ha',
        'Relación con una Hectáreas',
        2,
        environment.features.showPerfilUnidades,
        6,
        InputType.NUMERIC,
        '1 Hectáreas = X unidades'
      ),
      this.dropdownField<UserModel, FincasModel>('id_finca', 'Finca asociada', 3, this.fincas, environment.features.showPerfilFincas, 6, 'id'),
    ];
  }

  private getClientFields(): FormField<ClienteModel>[] {
    return [
      this.textField<ClienteModel>('nombre', 'Nombre/Razón social', 1, true, 6),
      this.textField<ClienteModel>('apellidos', 'Apellidos', 2, true, 6),
      this.textField<ClienteModel>('direccion', 'Dirección', 3, true, 6),
      this.textField<ClienteModel>('codigo_postal', 'Código postal', 4, true, 6, InputType.NUMERIC),
      this.textField<ClienteModel>('localidad', 'Población', 5, true, 6),
      this.textField<ClienteModel>('provincia', 'Provincia', 6, true, 6),
      this.textField<ClienteModel>('telefono', 'Telefono', 7, true, 6, InputType.NUMERIC),
      this.textField<ClienteModel>('nif', 'CIF/NIF', 8, true, 6),
      this.textField<ClienteModel>('ropo', 'ROPO', 9, true, 6),
    ];
  }

  private textField<T>(
    name: string,
    label: string,
    order: number,
    isVisible = true,
    width = 6,
    inputType: InputType = InputType.TEXT,
    placeholder?: string
  ): FormField<T> {
    return {
      name,
      vinculatedFields: undefined,
      label,
      placeholder,
      input: {
        type: inputType,
        isEditable: true,
      },
      width,
      conditions: this.conditions({ isVisible }),
      order,
    };
  }

  private dropdownField<T, TValue>(
    name: string,
    label: string,
    order: number,
    data: DropdownData<TValue>,
    isVisible = true,
    width = 6,
    valuePrimaryKey?: string
  ): FormField<T> {
    const input = {
      type: InputType.DROPDOWN,
      isEditable: true,
      data,
      ...(valuePrimaryKey ? { dropdownConfiguration: { valuePrimaryKey } } : {}),
    };

    return {
      name,
      vinculatedFields: undefined,
      label,
      input,
      width,
      conditions: this.conditions({ isVisible }),
      order,
    };
  }

  private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
    return {
      isRememberedOnDuplicate: true,
      isRequired: false,
      isRetained: false,
      hasUndoEnabled: false,
      isVisible: true,
      ...overrides,
    };
  }
}
